// CLIPS-HM · v2.21.8 · CONSULTA COMPARTIDA del estado del clip, por evento.
// POR QUÉ: con la vía rápida el clip puede estar listo en segundos, así que durante los primeros
// 15 s se pregunta cada 500 ms y después se vuelve a la cadencia normal. La consulta es ÚNICA por
// evento visible aunque haya varias superficies montadas, se cancela al salir y NO acelera ninguna
// consulta global (alarmas, cola, bitácora): sólo este endpoint y sólo para el evento a la vista.
import api, { API } from "@/lib/api";

const RAPIDO_MS = 500;
const NORMAL_MS = 6000;
const VENTANA_RAPIDA_MS = 15000;   // tras los primeros 15 s, cadencia normal
const LIMITE_MS = 150000;          // tope por tiempo: después deja de preguntar

const canales = new Map();

function nuevo(eventoId) {
  return { subs: new Set(), timer: null, enVuelo: null, seq: 0, aceptada: 0,
           inicio: Date.now(), ultimo: null, eventoId };
}

function emitir(c) {
  c.subs.forEach((cb) => cb(c.ultimo));
}

// DISPONIBLE sólo cuando el cierre está confirmado Y el archivo se puede leer: se pide el primer
// byte con el permiso recién emitido. Un «listo» sin archivo accesible se sigue mostrando como
// preparación, nunca como video disponible.
async function archivoAccesible(eventoId, ticket) {
  try {
    const r = await fetch(`${API}/eventos/${eventoId}/clip?t=${encodeURIComponent(ticket)}`,
      { headers: { Range: "bytes=0-0" } });
    return r.status === 206 || r.status === 200;
  } catch {
    return false;
  }
}

async function consultar(c) {
  if (c.enVuelo) return c.enVuelo;   // una sola consulta en vuelo por evento
  c.enVuelo = (async () => {
    const seq = ++c.seq;
    try {
      const r = await api.get(`/eventos/${c.eventoId}/clip/estado`);
      let dato = r.data;
      if (dato?.estado === "listo" && dato.ticket
          && !(await archivoAccesible(c.eventoId, dato.ticket))) {
        dato = { ...dato, estado: "preparando", etiqueta: "Preparando video" };
      }
      if (seq < c.aceptada) return;    // respuesta ANTIGUA: se descarta, no pisa a una más reciente
      c.aceptada = seq;
      c.ultimo = dato;
      emitir(c);
    } catch (e) {
      const codigo = e?.response?.status;
      if (seq < c.aceptada) return;
      c.aceptada = seq;
      // 404/403 = este evento no tiene superficie de video. Otro fallo es TRANSITORIO: se reintenta.
      c.ultimo = (codigo === 404 || codigo === 403)
        ? { estado: "no_aplica" }
        : (c.ultimo || { estado: "preparando", etiqueta: "Preparando video" });
      emitir(c);
    } finally {
      c.enVuelo = null;
    }
  })();
  return c.enVuelo;
}

function programar(c) {
  if (c.timer) clearTimeout(c.timer);
  const vivido = Date.now() - c.inicio;
  const estado = c.ultimo?.estado;
  if (!c.subs.size) return;
  if (estado && estado !== "preparando") return;   // listo, no disponible, expirado o no aplica
  if (vivido > LIMITE_MS) {
    // Se deja de preguntar, y se DICE: un spinner eterno sería prometer un video que no llegó.
    c.ultimo = { ...(c.ultimo || {}), estado: "espera_agotada" };
    emitir(c);
    return;
  }
  const espera = vivido < VENTANA_RAPIDA_MS ? RAPIDO_MS : NORMAL_MS;
  c.timer = setTimeout(async () => {
    c.timer = null;
    await consultar(c);
    programar(c);
  }, espera);
}

export function suscribirClip(eventoId, cb) {
  let c = canales.get(eventoId);
  if (!c) { c = nuevo(eventoId); canales.set(eventoId, c); }
  c.subs.add(cb);
  if (c.ultimo) cb(c.ultimo);
  if (c.subs.size === 1) {
    c.inicio = Date.now();
    consultar(c).then(() => programar(c));
  }
  return () => {
    c.subs.delete(cb);
    if (!c.subs.size) {               // nadie mirando: se cancela el sondeo y se olvida el canal
      if (c.timer) clearTimeout(c.timer);
      c.timer = null;
      canales.delete(eventoId);
    }
  };
}

export async function refrescarClip(eventoId) {
  const c = canales.get(eventoId) || nuevo(eventoId);
  // Si ya hay una consulta en vuelo se ESPERA a su respuesta: devolver la anterior daría un
  // permiso viejo y el reproductor abriría con un ticket a punto de caducar.
  await (c.enVuelo || consultar(c));
  // Si el permiso fresco dice que aún se prepara, se REANUDA el sondeo: el operador acaba de
  // pedir el video, así que la cuenta de espera empieza de nuevo en vez de dejar el panel parado.
  if (c.subs.size && c.ultimo?.estado === "preparando" && !c.timer) {
    c.inicio = Date.now();
    programar(c);
  }
  return c.ultimo;
}
